import { useId, useRef, useState } from "react";
import {
  pickerAddressLine,
  type AddressPicker,
} from "@shared-core/domain/customer-address-book";
import { Badge } from "../../../app/components/primitives/badge";
import { Button } from "../../../app/components/primitives/button";
import { AddressBookDialog, type AddressBookView } from "./AddressBookDialog";

/**
 * Where an item is delivered, as the buy box shows it: a summary of the chosen address (its name, lines, town and
 * postcode, and whether it is the person's default) and a Change button that opens the addresses in a modal, with each
 * address's own actions where the person has an address book and `canManage`. A person with no saved address sees that
 * said, with the way to add one.
 *
 * The summary is drawn for one address too; Change is there when there is another to choose, or an address to add.
 * `canManage` is off for one row of a delivery split across several addresses: that row only chooses.
 */
export function DeliveryAddressPicker({
  picker,
  chosenId,
  onChoose,
  onRemoved,
  reload,
  canManage,
  disabled,
  isInvalid = false,
  changeLabel = "Change delivery address",
}: {
  picker: AddressPicker;
  chosenId: string | null;
  onChoose: (addressId: string) => void;
  /** An address left the person's book: the page moves its choice off it if it was the one chosen. */
  onRemoved: (addressId: string) => void;
  reload: () => void;
  canManage: boolean;
  disabled: boolean;
  /** The server refused the address named: the summary is outlined in red (its words are shown by the page). */
  isInvalid?: boolean;
  /** The Change button's accessible name: unique where there are several pickers on the page. */
  changeLabel?: string;
}) {
  const labelId = useId();
  const openerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [startOn, setStartOn] = useState<AddressBookView>("list");
  const isBook = picker.source === "address_book";
  const canAdd = canManage && isBook && picker.add_url !== null;
  const chosen = picker.addresses.find((a) => a.address_id === chosenId);

  function open(view: AddressBookView) {
    setStartOn(view);
    setIsOpen(true);
  }

  const dialog = (
    <AddressBookDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      startOn={startOn}
      picker={picker}
      chosenId={chosenId}
      canManage={canManage}
      onChoose={onChoose}
      onRemoved={onRemoved}
      reload={reload}
      returnFocusTo={openerRef}
    />
  );

  if (chosen === undefined) {
    // An empty book: the only way to order is to add an address, so that is what is offered.
    return (
      <>
        <div className="space-y-2">
          {isBook && picker.addresses.length === 0 && (
            <p role="note" className="text-muted-foreground text-sm">
              You have no saved delivery addresses yet.
            </p>
          )}
          {canAdd && (
            <Button
              ref={openerRef}
              variant="outline"
              disabled={disabled}
              onClick={() => open("add")}
            >
              Add an address
            </Button>
          )}
        </div>
        {dialog}
      </>
    );
  }

  const line = pickerAddressLine(chosen);
  const hasChoice = picker.addresses.length > 1 || canAdd;
  return (
    <>
      <div className="space-y-1.5">
        <p id={labelId} className="text-sm leading-none font-medium">
          Deliver to
        </p>
        <div
          role="group"
          aria-labelledby={labelId}
          className={`flex items-start justify-between gap-3 rounded-md border p-3 ${isInvalid ? "border-destructive" : ""}`}
        >
          <div className="min-w-0 text-sm">
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-medium">{chosen.name}</span>
              {isBook && chosen.is_default && (
                <Badge variant="secondary">Your default</Badge>
              )}
            </p>
            {line && <p className="text-muted-foreground">{line}</p>}
            <p className="text-muted-foreground">{chosen.postcode}</p>
          </div>
          {hasChoice && (
            <Button
              ref={openerRef}
              variant="outline"
              size="sm"
              aria-label={changeLabel}
              disabled={disabled}
              onClick={() => open("list")}
            >
              Change
            </Button>
          )}
        </div>
      </div>
      {dialog}
    </>
  );
}
